import {
    SkyNetTableConfig, SkyNetTableField,
} from 'app/shared-components/SkyNetTable'
import {
    CommonRoutes, FilterType,
} from 'app/enums/common.enums'
import {
    ContainersTablesTab,
} from 'app/enums/domain.enums'
import CloudIconBoolean from 'app/common/DomainObject/ComponentMapper/CloudIconBoolean'
import getReadingThresholdType, {
    ReadingThresholdType,
} from 'app/utils/getReadingThresholdType'
import {
    convertFromFilterDaysDate,
    convertToFilterDaysDate,
    parseFromFilterDaysDate,
    parseToFilterDaysDate,
} from 'app/utils/date'
import {
    LoggerType,
} from 'app/Apps/LoggersAndGateways/Loggers'

import {
    Containers,
} from './containers.types'

const daysFilterOptions = {
    labels: {
        from: 'MORE_OR_EQUAL_DAYS',
        to: 'LESS_THAN_DAYS',
    },
    convert: {
        from: convertFromFilterDaysDate,
        to: convertToFilterDaysDate,
    },
    parse: {
        from: parseFromFilterDaysDate,
        to: parseToFilterDaysDate,
    },
}

const temperatureFilterOptions = {
    labels: {
        from: 'MIN_TEMPERATURE',
        to: 'MAX_TEMPERATURE',
    },
}

const longTermLease: SkyNetTableField<keyof Containers> = {
    name: 'currentLongTermLeaseNumber',
    sorting: false,
    width: '100px',
    labelKey: 'LT_CONTRACT',
}

const defaultFields: SkyNetTableField<keyof Containers>[] = [
    {
        name: 'serialNumber',
        reduced: true,
        width: '130px',
        labelKey: 'SERIAL_NUMBER',
    },
    {
        name: 'containerTypeContainerModel',
        filterType: FilterType.Array,
        filterField: 'containerTypeContainerModel',
        width: '170px',
        labelKey: 'CONTAINER_MODEL',
    },
    {
        name: 'tempRange',
        filterType: FilterType.Array,
        filterField: 'tempRange',
        componentName: 'ContainerTempRange',
        width: '150px',
        labelKey: 'RANGE',
    },
    {
        name: 'containerStatusEnriched',
        filterType: FilterType.Array,
        reduced: true,
        filterField: 'containerStatusEnriched',
        componentName: 'ContainerStatusEnriched',
        minWidth: '200px',
        labelKey: 'STATUS',
    },
    {
        name: 'containerStatusEnrichedChangedOnUtc',
        componentName: 'StatusAge',
        width: '160px',
        labelKey: 'STATUS_AGE',
    },
    {
        name: 'currentOrderOrderNumber',
        filterField: 'currentOrderOrderNumber',
        filterType: FilterType.Array,
        width: '120px',
        labelKey: 'CURRENT_ORDER',
    },
    {
        name: 'locationLocationName',
        filterField: 'locationLocationName',
        filterType: FilterType.Array,
        reduced: true,
        minWidth: '200px',
        labelKey: 'LOCATION',
    },
    {
        name: 'defaultInternalLoggerType',
        filterField: 'defaultInternalLoggerType',
        minWidth: '200px',
        filterType: FilterType.Array,
        componentName: 'EnumValue',
        allowedValuesEnum: LoggerType,
        labelKey: 'DEFAULT_LOGGER_INTERNAL',

    },
    {
        name: 'batteryLevelInternal',
        labelKey: 'BATTERY_LEVEL',
        minWidth: '115px',
        mapCellProps: ({
            batteryLevelInternal,
        }) => {
            return {
                children: batteryLevelInternal && `${Math.round(batteryLevelInternal * 100)}%`,
            }
        },
    },
    {
        name: 'lastMeasuredTempInternal',
        componentName: 'ContainerTemp',
        propsMapper: {
            lowerTempBoundary: 'containerTypeIntTempRangeStart',
            higherTempBoundary: 'containerTypeIntTempRangeEnd',
        },
        width: '100px',
        sorting: false,
        filterField: 'lastMeasuredTempInternal',
        filterType: FilterType.IntegerRange,
        filterOptions: temperatureFilterOptions,
        labelKey: 'INTERNAL',
    },
    {
        name: 'connectedInternal',
        labelKey: 'CONNECTED?',
        width: '90px',
        sorting: false,
        mapCellProps: ({
            lastMeasuredOnUtcInternal,
        }) => {
            return {
                value: getReadingThresholdType(lastMeasuredOnUtcInternal)
                    === ReadingThresholdType.LIVE,
            }
        },
        Cell: CloudIconBoolean,
    },
    {
        name: 'lastMeasuredOnUtcInternal',
        componentName: 'Age',
        width: '120px',
        sorting: false,
        filterField: 'lastMeasuredOnUtcInternal',
        filterType: FilterType.IntegerRange,
        filterOptions: daysFilterOptions,
        labelKey: 'DATA_AGE',
    },
    {
        name: 'loggerExchangedOnUtcInternal',
        componentName: 'LoggerAge',
        width: '150px',
        filterField: 'loggerExchangedOnUtcInternal',
        filterType: FilterType.IntegerRange,
        filterOptions: daysFilterOptions,
        labelKey: 'LOGGER_AGE',
    },
    {
        name: 'defaultAmbientLoggerType',
        filterField: 'defaultAmbientLoggerType',
        minWidth: '200px',
        filterType: FilterType.Array,
        componentName: 'EnumValue',
        allowedValuesEnum: LoggerType,
        labelKey: 'DEFAULT_LOGGER_AMBIENT',
    },
    {
        name: 'batteryLevelAmbient',
        labelKey: 'BATTERY_LEVEL',
        minWidth: '115px',
        mapCellProps: ({
            batteryLevelAmbient,
        }) => {
            return {
                children: batteryLevelAmbient && `${Math.round(batteryLevelAmbient * 100)}%`,
            }
        },
    },
    {
        name: 'lastMeasuredTempAmbient',
        componentName: 'ContainerTemp',
        width: '100px',
        sorting: false,
        filterField: 'lastMeasuredTempAmbient',
        filterType: FilterType.IntegerRange,
        filterOptions: temperatureFilterOptions,
        labelKey: 'AMBIENT',
    },
    {
        name: 'connectedAmbient',
        labelKey: 'CONNECTED?',
        width: '90px',
        sorting: false,
        mapCellProps: ({
            lastMeasuredOnUtcAmbient,
        }) => {
            return {
                value: getReadingThresholdType(lastMeasuredOnUtcAmbient)
                    === ReadingThresholdType.LIVE,
            }
        },
        Cell: CloudIconBoolean,
    },
    {
        name: 'lastMeasuredOnUtcAmbient',
        componentName: 'Age',
        width: '120px',
        sorting: false,
        filterField: 'lastMeasuredOnUtcAmbient',
        filterType: FilterType.IntegerRange,
        filterOptions: daysFilterOptions,
        labelKey: 'DATA_AGE',
    },
    {
        name: 'loggerExchangedOnUtcAmbient',
        componentName: 'LoggerAge',
        width: '120px',
        filterField: 'loggerExchangedOnUtcAmbient',
        filterType: FilterType.IntegerRange,
        filterOptions: daysFilterOptions,
        labelKey: 'LOGGER_AGE',
    },
]

const fieldsWithLongTermLease = [
    ...defaultFields.slice(0, 5),
    longTermLease,
    ...defaultFields.slice(5),
]

const tableConfig: SkyNetTableConfig<keyof Containers> = {
    name: 'ContainersTable',
    tabs: [
        {
            name: 'OPERATIONAL_CONTAINERS',
            url: ContainersTablesTab.Operational,
            defaultTab: true,
            fields: fieldsWithLongTermLease,
        },
        {
            name: 'ALL_CONTAINERS',
            url: CommonRoutes.All,
            fields: fieldsWithLongTermLease,
        },
        {
            name: 'EXCLUDED',
            url: CommonRoutes.Excluded,
            fields: defaultFields,
        },
    ],

}

export default tableConfig
